import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import Loader from "../component/Loader";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { BASE_URL } from "../api";
import { fetchRealAccounts } from "../hooks/useRealAccounts";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders } from "../utils/helpers";
import CommonTable from "../component/CommonTable";
import dayjs from "dayjs";
import MobileFilter from "../component/MobileFilter";

const formatDateTime = (dateString) => {
  if (!dateString || dateString === "-") return "-";
  const dateStr = dateString.toString().split(".")[0];
  const dateObj = new Date(dateStr);
  if (isNaN(dateObj.getTime())) return dateString;

  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

// Empty API values (null / undefined / "") are shown as "-"; 0 is a real value and is kept
const showValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

const formatDuration = (seconds) => {
  const total = Number(seconds);
  if (seconds === null || seconds === undefined || seconds === "" || isNaN(total) || total < 0) return "-";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = Math.floor(total % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

// With a currency the amount is shown as money: +$0.36, -$0.26, $0.00
const renderSignedAmount = (value, currency = "") => {
  if (value === null || value === undefined || value === "" || value === "-") return "-";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  const sign = num > 0 ? "+" : num < 0 ? "-" : "";
  const text = currency
    ? `${sign}${currency}${Math.abs(num).toFixed(2)}`
    : (num > 0 ? `+${value}` : value);
  return (
    <span className={num > 0 ? "text-success" : num < 0 ? "text-danger" : ""}>
      {text}
    </span>
  );
};

const renderTradeStatusBadge = (status) => {
  if (!status || status === "-") return "-";
  const upper = String(status).toUpperCase();
  if (upper === "CLOSED") {
    return <span className="badge bg-secondary-subtle text-secondary px-2 py-1 font-size-11">CLOSED</span>;
  }
  if (upper === "OPEN") {
    return <span className="badge bg-primary-subtle text-primary px-2 py-1 font-size-11">OPEN</span>;
  }
  return status;
};

const renderTradeTypeBadge = (type) => {
  if (!type || type === "-") return "-";
  const upper = String(type).toUpperCase();
  if (upper === "BUY") {
    return <span className="badge bg-success-subtle text-success px-2 py-1 font-size-12">BUY</span>;
  }
  if (upper === "SELL") {
    return <span className="badge bg-danger-subtle text-danger px-2 py-1 font-size-12">SELL</span>;
  }
  return type;
};

// Card with a title bar, used for each block of the trade details modal
const DetailCard = ({ title, icon, children }) => (
  <div className="border rounded-3 h-100 overflow-hidden bg-white">
    <div className="px-3 py-2 bg-light border-bottom d-flex align-items-center gap-2 text-uppercase font-size-12 fw-bold text-primary">
      <i className={`bx ${icon} fs-5`} />
      {title}
    </div>
    <ul className="list-group list-group-flush">{children}</ul>
  </div>
);

// One "label ......... value" line inside a DetailCard
const DetailRow = ({ label, children, mono = false, noTranslate = false, strong = false }) => (
  <li className={`list-group-item d-flex justify-content-between align-items-center gap-3 px-3 py-2 ${strong ? "bg-light" : ""}`}>
    <span className={`font-size-13 ${strong ? "fw-bold text-dark" : "text-muted"}`}>{label}</span>
    <span
      className={`text-dark text-end text-break ${strong ? "fw-bold font-size-15" : "fw-semibold font-size-13"} ${mono ? "font-monospace" : ""} ${noTranslate ? "notranslate" : ""}`}
      translate={noTranslate ? "no" : undefined}
    >
      {children}
    </span>
  </li>
);

// Account Type filter: only Live is offered. Set SHOW_DEMO_ACCOUNT_TYPE to true to bring Demo back.
const SHOW_DEMO_ACCOUNT_TYPE = false;
const DEFAULT_ACCOUNT_TYPE = "live";

export default function TradeHistory() {
  const handleError = useHandleError();
  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const weekAgoDate = oneWeekAgo.toISOString().split("T")[0];
  const mid = sessionStorage.getItem("mid");

  const [loading, setLoading] = useState(false);
  const [tradeData, setTradeData] = useState([]);
  // Details Modal state (holds the full API row of the selected trade)
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedDates, setAppliedDates] = useState({
    fromDate: weekAgoDate,
    toDate: today,
  });
  const [accounts, setAccounts] = useState([]);
  const accountCacheRef = useRef({
    live: null,
    demo: null
  });
  const [filters, setFilters] = useState({
    accountType: DEFAULT_ACCOUNT_TYPE,
    fromDate: weekAgoDate,
    toDate: today,
    tradeType: "open",
    account: "",
  });

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedTrade(null);
      }
    };
    if (selectedTrade) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedTrade]);

  const clearFilters = () => {
    setFilters({
      accountType: DEFAULT_ACCOUNT_TYPE,
      fromDate: "",
      toDate: "",
      tradeType: "open",
      account: "",
    });
    setSearchTerm("");
    setSearchQuery("");
    setAppliedDates({
      fromDate: "",
      toDate: "",
    });
    setTradeData([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "accountType") {
      setFilters(prev => ({ ...prev, accountType: value, account: "" }));
      setAccounts([]);
      const list = await getAllAccounts(value);
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters(prev => ({ ...prev, accountType: value, account: firstAcc }));
      if (firstAcc) {
        getTradeData({
          ...filters,
          accountType: value,
          account: firstAcc
        });
      }
      return;
    }

    setFilters(prev => {
      let updatedFilter = { ...prev, [name]: value };

      if (name === "fromDate" && dayjs(value).isAfter(dayjs(prev.toDate))) {
        updatedFilter.toDate = value;
      }

      if (name === "toDate" && dayjs(value).isBefore(dayjs(prev.fromDate))) {
        updatedFilter.toDate = prev.fromDate;
      }

      return updatedFilter;
    });
  };

  const getAllAccounts = async (type) => {
    if (!type) return [];

    if (accountCacheRef.current[type]) {
      setAccounts(accountCacheRef.current[type]);
      return accountCacheRef.current[type];
    }

    const url =
      type === "live"
        ? `${BASE_URL}/MT5M/real_accounts_number_list`
        : `${BASE_URL}/MT5M/demo_account_number_list`;

    try {
      setLoading(true);
      const res = type === "live"
        ? await fetchRealAccounts()
        : await axios.get(url, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        const list = res.data.data || [];

        accountCacheRef.current[type] = list;

        setAccounts(list);
        return list;

      } else {
        MessageToster("error", res.data.message || "Failed to fetch accounts");
        return [];
      }
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initPage = async () => {
      const list = await getAllAccounts(DEFAULT_ACCOUNT_TYPE);
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters(prev => ({
        ...prev,
        accountType: DEFAULT_ACCOUNT_TYPE,
        account: firstAcc
      }));
      if (firstAcc) {
        getTradeData({
          ...filters,
          accountType: DEFAULT_ACCOUNT_TYPE,
          account: firstAcc
        });
      }
    };
    initPage();
  }, []);

  const getTradeData = async (customFilters = filters) => {

    if (!customFilters.accountType) {
      return MessageToster("error", "Please select account type");
    }
    if (!customFilters.account) {
      return MessageToster("error", "Please select account number");
    }

    if (dayjs(customFilters.toDate).isBefore(dayjs(customFilters.fromDate))) {
      return MessageToster("error", "To Date cannot be earlier than From Date");
    }


    setLoading(true);
    try {

      const endpoint =
        customFilters.accountType === "live"
          ? `${BASE_URL}/account/get_live_trades`
          : `${BASE_URL}/account/get_demo_trades`;

      const params = {
        loginid: Number(customFilters.account),
        from_date: customFilters.fromDate,
        to_date: customFilters.toDate,
      };

      const res = await axios.get(endpoint, {
        params,
        headers: getApiHeaders(),
      });

      const rawData =
        Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.trades)
            ? res.data.trades
            : Array.isArray(res?.data?.trade_list)
              ? res.data.trade_list
              : Array.isArray(res?.data?.list)
                ? res.data.list
                : Array.isArray(res?.data)
                  ? res.data
                  : [];

      const isSuccess =
        res.data &&
        (res.data.code === 200 ||
          res.data.status === "success" ||
          res.data.status === true ||
          res.status === 200);

      if (isSuccess && rawData.length > 0) {
        setSearchQuery(searchTerm);
        setAppliedDates({
          fromDate: customFilters.fromDate,
          toDate: customFilters.toDate,
        });
        const formattedData = rawData.map((item) => ({
          Account: item.account_number ?? "-",
          Position_ID: item.position_id ?? "-",
          Ticket: item.entry_ticket ?? item.trade_id ?? "-",
          Symbol: item.symbol ?? "-",
          Volume: item.volume ?? "-",
          // Trade: item.trade_status ?? "-",
          Type: item.trade_type ?? "-",
          Time: item.exit_time ?? "-",
          Open_Time: item.exit_time ?? "-",
          Price: item.entry_price ?? "-",
          Open_Price: item.entry_price ?? "-",
          "T/P": item.tp ?? "-",
          "S/L": item.sl ?? "-",
          Commission: item.commission ?? 0,
          Swap: item.swap_amount ?? 0,
          Profit: item.net_profit ?? 0,
          raw: item,
        }));
        setTradeData(formattedData);

      } else {
        setTradeData([]);
        setAppliedDates({
          fromDate: customFilters.fromDate,
          toDate: customFilters.toDate,
        });
        if (res.data && res.data.code && res.data.code !== 200 && res.data.status !== "success" && res.data.status !== true) {
          MessageToster("error", res.data.message || "Failed to fetch trade history");
        }
      }
    }
    catch (error) {
      setTradeData([]);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = tradeData.filter((item) => {
    // Frontend Date filter: ONLY applied on Search click (via appliedDates)
    if (appliedDates.fromDate || appliedDates.toDate) {
      const exitDateStr = item.Time ? String(item.Time).split(/[T ]/)[0] : "";
      if (exitDateStr) {
        if (appliedDates.fromDate && exitDateStr < appliedDates.fromDate) return false;
        if (appliedDates.toDate && exitDateStr > appliedDates.toDate) return false;
      }
    }

    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.Account).toLowerCase().includes(query) ||
      String(item.Position_ID).toLowerCase().includes(query) ||
      String(item.Symbol).toLowerCase().includes(query) ||
      String(item.Volume).toLowerCase().includes(query) ||
      // String(item.Trade).toLowerCase().includes(query) ||
      String(item.Type).toLowerCase().includes(query) ||
      String(item.Time).toLowerCase().includes(query) ||
      String(item.Price).toLowerCase().includes(query) ||
      String(item["T/P"]).toLowerCase().includes(query) ||
      String(item["S/L"]).toLowerCase().includes(query) ||
      String(item.Commission).toLowerCase().includes(query) ||
      String(item.Swap).toLowerCase().includes(query) ||
      String(item.Profit).toLowerCase().includes(query)
    );
  });

  // Exported sheet has the same columns, in the same order, as the table
  const getExportRows = () =>
    filteredData.map((row, idx) => ({
      "No.": idx + 1,
      "Time": formatDateTime(row.Time || row.Open_Time),
      "Account": row.Account,
      "Position ID": row.Position_ID,
      "Symbol": row.Symbol,
      "Volume": row.Volume,
      // "Trade": row.Trade,
      "Type": row.Type,
      "Price": row.Price,
      "T/P": row["T/P"],
      "S/L": row["S/L"],
      "Commission": row.Commission,
      "Swap": row.Swap,
      "Profit/Loss ($)": row.Profit,
    }));

  return (
    <>
      <div id="layout-wrapper">
        {loading &&
          <Loader />
        }
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* start page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">Trade History</h2>
                  </div>
                </div>
              </div>
              {/* end page title */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                        <div>
                          <label htmlFor="">From</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="fromDate"
                            max={filters.toDate || today}
                            value={filters.fromDate}
                            onChange={handleChange}
                            id="example-date-input"
                          />
                        </div>
                        <div>
                          <label htmlFor="">To</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="toDate"
                            min={filters.fromDate || undefined}
                            max={today}
                            value={filters.toDate}
                            onChange={handleChange}
                            id="example-date-input"
                          />
                        </div>
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            {SHOW_DEMO_ACCOUNT_TYPE && <option value="demo">Demo</option>}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account No</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={() => getTradeData(filters)}
                          >
                            Search
                          </button>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={clearFilters}
                            disabled={!filters.fromDate && !filters.toDate && !filters.account && !searchTerm}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="ms-auto">
                          <ExportButton
                            disabled={!filteredData || filteredData.length === 0}
                            onClick={() => { exportToExcel(getExportRows(), "trade_history", "default") }}
                          />
                        </div>
                      </div>

                      <MobileFilter
                        title="History"
                        onExport={() => { exportToExcel(getExportRows(), "trade_history", "default") }}
                        exportDisabled={!filteredData || filteredData.length === 0}
                      >
                        <div>
                          <label htmlFor="">From</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="fromDate"
                            max={filters.toDate || today}
                            value={filters.fromDate}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label htmlFor="">To</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="toDate"
                            min={filters.fromDate || undefined}
                            max={today}
                            value={filters.toDate}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            {SHOW_DEMO_ACCOUNT_TYPE && <option value="demo">Demo</option>}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account No</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light flex-grow-1"
                            onClick={() => getTradeData(filters)}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-light waves-effect waves-light border flex-grow-1"
                            onClick={clearFilters}
                            disabled={!filters.fromDate && !filters.toDate && !filters.account && !searchTerm}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Clear
                          </button>
                        </div>
                      </MobileFilter>
                      <CommonTable
                        columns={[
                          {
                            header: "Time",
                            render: (row) => formatDateTime(row.Time || row.Open_Time)
                          },
                          { header: "Account", accessor: "Account" },
                          { header: "Position ID", accessor: "Position_ID" },
                          { header: "Symbol", accessor: "Symbol" },
                          { header: "Volume", accessor: "Volume" },
                          // {
                          //   header: "Trade",
                          //   render: (row) => renderTradeStatusBadge(row.Trade)
                          // },
                          {
                            header: "Type",
                            render: (row) => renderTradeTypeBadge(row.Type)
                          },
                          { header: "Price", accessor: "Price" },
                          { header: "T/P", accessor: "T/P" },
                          { header: "S/L", accessor: "S/L" },
                          { header: "Commission", accessor: "Commission" },
                          { header: "Swap", accessor: "Swap" },
                          {
                            header: "Profit/Loss",
                            render: (row) => (
                              <span className="fw-semibold">{renderSignedAmount(row.Profit, "$")}</span>
                            )
                          },
                          {
                            header: "Action",
                            render: (row) => (
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 px-2 py-1"
                                onClick={() => setSelectedTrade(row.raw)}
                                title="View Trade Details"
                              >
                                <i className="bx bx-show fs-6" />
                                <span>Details</span>
                              </button>
                            )
                          }
                        ]}
                        data={filteredData}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>{" "}
                {/* end col */}
              </div>
            </div>
            {/* container-fluid */}
          </div>

          {/* End Page-content */}
          <Footer />
        </div>
      </div>

      {/* Trade Details Modal */}
      {selectedTrade && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1050 }}
            onClick={() => setSelectedTrade(null)}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1055 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedTrade(null);
            }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: "760px" }}>
              <div
                className="modal-content"
                style={{ borderRadius: "16px", overflow: "hidden" }}
              >
                {/* Modal Header */}
                <div className="modal-header">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle"
                      style={{ width: "38px", height: "38px" }}
                    >
                      <i className="bx bx-line-chart fs-4" />
                    </div>
                    <div>
                      <h5 className="modal-title mb-0">Trade Details</h5>
                      <small className="text-muted">
                        Position ID:{" "}
                        <span className="font-monospace fw-semibold text-dark">
                          {showValue(selectedTrade.position_id)}
                        </span>
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedTrade(null)}
                    aria-label="Close"
                  />
                </div>

                {/* Modal Body */}
                <div className="modal-body p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                  {/* Profit & Status Top Banner (tinted green for profit, red for loss) */}
                  <div
                    className={`p-3 mb-3 rounded-3 border d-flex flex-wrap align-items-center justify-content-between gap-3 ${
                      Number(selectedTrade.net_profit) > 0
                        ? "bg-success-subtle border-success-subtle"
                        : Number(selectedTrade.net_profit) < 0
                          ? "bg-danger-subtle border-danger-subtle"
                          : "bg-light"
                    }`}
                  >
                    <div>
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <span className="fs-5 fw-bold text-dark notranslate" translate="no">
                          {showValue(selectedTrade.symbol)}
                        </span>
                        {renderTradeTypeBadge(selectedTrade.trade_type)}
                        {/* {renderTradeStatusBadge(selectedTrade.trade_status)} */}
                      </div>
                      <div className="text-muted font-size-12 mt-1">
                        <i className="bx bx-time-five me-1" />
                        Duration: {formatDuration(selectedTrade.duration_sec)}
                      </div>
                    </div>

                    <div className="text-end">
                      <div className="text-muted font-size-12 fw-medium text-uppercase">
                        Net Profit/Loss
                      </div>
                      <div className="fs-3 fw-bold lh-1 mt-1">
                        {renderSignedAmount(selectedTrade.net_profit, "$")}
                      </div>
                    </div>
                  </div>

                  {/* Entry and Exit side by side */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <DetailCard title="Entry" icon="bx-log-in-circle">
                        <DetailRow label="Time">{formatDateTime(selectedTrade.entry_time)}</DetailRow>
                        <DetailRow label="Price">{showValue(selectedTrade.entry_price)}</DetailRow>
                        <DetailRow label="Lots">{showValue(selectedTrade.entry_lots)}</DetailRow>
                        <DetailRow label="Ticket" mono>{showValue(selectedTrade.entry_ticket)}</DetailRow>
                        <DetailRow label="Spread">{showValue(selectedTrade.entry_spread)}</DetailRow>
                      </DetailCard>
                    </div>
                    <div className="col-md-6">
                      <DetailCard title="Exit" icon="bx-log-out-circle">
                        <DetailRow label="Time">{formatDateTime(selectedTrade.exit_time)}</DetailRow>
                        <DetailRow label="Price">{showValue(selectedTrade.exit_price)}</DetailRow>
                        <DetailRow label="Lots">{showValue(selectedTrade.exit_lots)}</DetailRow>
                        <DetailRow label="Ticket" mono>{showValue(selectedTrade.exit_ticket)}</DetailRow>
                        <DetailRow label="Spread">{showValue(selectedTrade.exit_spread)}</DetailRow>
                      </DetailCard>
                    </div>
                  </div>

                  {/* Trade info and money breakdown side by side */}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <DetailCard title="Trade Information" icon="bx-info-circle">
                        <DetailRow label="Account" mono>{showValue(selectedTrade.account_number)}</DetailRow>
                        <DetailRow label="Trade ID" mono>{showValue(selectedTrade.trade_id)}</DetailRow>
                        <DetailRow label="Volume">{showValue(selectedTrade.volume)}</DetailRow>
                        <DetailRow label="Volume Closed">{showValue(selectedTrade.volume_closed_value)}</DetailRow>
                        <DetailRow label="T/P">{showValue(selectedTrade.tp)}</DetailRow>
                        <DetailRow label="S/L">{showValue(selectedTrade.sl)}</DetailRow>
                      </DetailCard>
                    </div>
                    <div className="col-md-6">
                      <DetailCard title="Profit & Charges" icon="bx-dollar-circle">
                        <DetailRow label="Gross Profit">{renderSignedAmount(selectedTrade.gross_profit)}</DetailRow>
                        <DetailRow label="Commission">{showValue(selectedTrade.commission)}</DetailRow>
                        <DetailRow label="Swap">{showValue(selectedTrade.swap_amount)}</DetailRow>
                        <DetailRow label="Fee">{showValue(selectedTrade.fee)}</DetailRow>
                        <DetailRow label="Net Profit/Loss" strong>{renderSignedAmount(selectedTrade.net_profit, "$")}</DetailRow>
                      </DetailCard>
                    </div>
                  </div>

                  {selectedTrade.comment && (
                    <div className="mt-3 px-3 py-2 bg-light border rounded-3 d-flex align-items-center gap-2">
                      <i className="bx bx-comment-dots text-muted fs-5" />
                      <span className="text-muted font-size-13">Comment:</span>
                      <span className="fw-semibold text-dark font-size-13 text-break notranslate" translate="no">
                        {selectedTrade.comment}
            
                      </span>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary px-4"
                    onClick={() => setSelectedTrade(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}